import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { FAQ } from "@/components/FAQ";
import { Reveal } from "@/components/Reveal";
import { OptImg } from "@/components/OptImg";
import { SITE } from "@/lib/site";

import { useSuspenseQuery } from "@tanstack/react-query";
import { listPublishedPosts } from "@/lib/content.functions";
import { formatDateTimeBR } from "@/lib/format";
import heroImg from "@/assets/hero.jpg?format=webp&quality=72&w=1600&imagetools";
import heroImgMobile from "@/assets/hero.jpg?format=webp&quality=70&w=800&imagetools";
import { TEAM } from "@/lib/team";
import { REVIEWS } from "@/lib/reviews";
import { CATEGORIES } from "@/lib/blog";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Concurso Astor — Conteúdo para empresas e empreendedores");
const DESC = seoDescription(
  "Plataforma editorial com artigos, análises e dicas sobre gestão, estratégia, finanças, marketing e vendas para empresas e empreendedores.",
);

const faq = [
  { q: "O que é o Concurso Astor?", a: "É uma plataforma editorial independente com artigos, análises e dicas práticas sobre gestão, estratégia, finanças, marketing, vendas e produtividade para empresas e empreendedores." },
  { q: "Para quem é o conteúdo?", a: "Para donos de empresa, sócios, gestores, fundadores e empreendedores que buscam informação de qualidade para tomar decisões melhores no seu negócio." },
  { q: "Qual é a linha editorial?", a: "Priorizamos texto direto, apuração cuidadosa e conteúdo acionável. Cobrimos gestão, estratégia, finanças, vendas, marketing, produtividade, inovação, tecnologia e cases de empresas brasileiras." },
  { q: "Quem escreve os artigos?", a: "O time editorial é apresentado na página Time editorial. Cada artigo passa por revisão antes da publicação e segue os princípios da nossa linha editorial." },
  { q: "Com que frequência publicam?", a: "Publicamos novos conteúdos ao longo da semana. Navegue por categorias para encontrar o material mais relevante para o seu momento profissional." },
];


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/" },
    ],
    links: [
      { rel: "canonical", href: `${SITE.baseUrl}/` },
      { rel: "preload", as: "image", href: heroImgMobile, fetchPriority: "high", media: "(max-width: 768px)" },
      { rel: "preload", as: "image", href: heroImg, fetchPriority: "high", media: "(min-width: 769px)" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  loader: async ({ context }) => {
    // Prefetch para que as últimas notícias apareçam já no SSR/primeiro render.
    await context.queryClient.ensureQueryData({
      queryKey: ["public-posts"],
      queryFn: () => listPublishedPosts(),
      staleTime: 60_000,
    });
  },
  component: Home,
});

const editorialPromises = [
  { t: "Gestão que funciona", d: "Frameworks, rotinas e ferramentas de gestão testados por empresas de diferentes portes." },
  { t: "Estratégia e negócios", d: "Modelos de negócio, posicionamento, crescimento sustentável e decisões estratégicas explicadas com clareza." },
  { t: "Finanças da empresa", d: "Fluxo de caixa, precificação, custos, impostos e captação — sem jargão desnecessário." },
  { t: "Vendas e marketing", d: "Aquisição de clientes, funil, tráfego pago, branding e como escalar receita de forma sustentável." },
  { t: "Empreendedorismo", d: "Dicas para quem está começando ou escalando: da validação de ideia à captação e ao crescimento." },
  { t: "Produtividade e times", d: "Como organizar rotinas, contratar bem e formar times enxutos de alta performance." },
];

const why = [
  "Time editorial identificado, com responsáveis por cada artigo",
  "Conteúdo baseado em fontes, dados e boas referências do mundo dos negócios",
  "Foco em acionáveis: cada texto entrega algo aplicável na próxima semana",
  "Linguagem direta, sem jargão vazio ou motivação genérica",
  "Site leve, carregamento rápido em qualquer conexão",
  "Publicações regulares alinhadas com o que empresas e empreendedores realmente enfrentam",
];


function Home() {
  const { data: dbPosts = [] } = useSuspenseQuery({
    queryKey: ["public-posts"],
    queryFn: () => listPublishedPosts(),
    staleTime: 60_000,
  });
  const featured = dbPosts[0];
  const latestPosts = dbPosts.slice(0, 6);
  const recentPosts = dbPosts.slice(1, 5);
  const highlightCats = CATEGORIES.slice(0, 6);

  return (
    <>
      <section className="hero-bg relative overflow-hidden">
        <div className="container-svg relative pt-16 pb-0 md:pt-24">
          <div className="mx-auto max-w-4xl text-center">
            <span className="eyebrow">Plataforma editorial</span>
            <h1 className="mt-6 text-4xl font-bold tracking-tight md:text-6xl lg:text-7xl leading-[1.05]">
              <span className="text-gradient">Concurso Astor</span>
              <span className="block mt-2 text-foreground">conteúdo estratégico para quem toca uma empresa.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
              Artigos, análises e dicas sobre gestão, estratégia, finanças, marketing e vendas — para decisões de negócio com mais clareza.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/blog" className="btn-accent">Ler os artigos</Link>
              <Link to="/sobre" className="btn-ghost">Sobre a plataforma</Link>
            </div>
          </div>

          <Reveal>
            <div className="relative mt-14 md:mt-20">
              <div className="pointer-events-none absolute -inset-x-6 -top-6 -bottom-10 rounded-[2rem] bg-gradient-to-b from-primary/10 via-transparent to-background blur-2xl" aria-hidden />
              <img
                src={heroImg}
                srcSet={`${heroImgMobile} 800w, ${heroImg} 1600w`}
                sizes="(max-width: 768px) 92vw, 92vw"
                alt="Escritório executivo contemporâneo ao entardecer, vista panorâmica da cidade"
                width={1600}
                height={1000}
                fetchPriority="high"
                decoding="async"
                className="relative mx-auto aspect-[16/9] w-full max-w-5xl rounded-2xl border border-border object-cover shadow-[var(--shadow-glow)]"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent" aria-hidden />
            </div>
          </Reveal>
        </div>
      </section>


      {featured && (
        <Section>
          <SectionHeader eyebrow="Em destaque" title="Leitura em foco" />
          <Link
            to="/blog/$slug"
            params={{ slug: featured.slug }}
            className="mt-10 grid gap-6 rounded-lg border border-border bg-card p-6 md:grid-cols-[1.3fr_1fr] md:p-8 hover:shadow-[var(--shadow-glow)] transition-shadow"
          >
            {featured.cover_image && (
              <OptImg
                src={featured.cover_image}
                alt={featured.title}
                width={800}
                height={500}
                className="h-full w-full rounded-md object-cover"
              />
            )}
            <div className="flex flex-col justify-center">
              <span className="eyebrow">{featured.category}</span>
              <h2 className="mt-3 text-2xl font-bold leading-tight md:text-3xl">{featured.title}</h2>
              <p className="mt-3 line-clamp-4 text-muted-foreground">{featured.excerpt}</p>
              <p className="mt-4 text-xs text-muted-foreground">{featured.published_at ? formatDateTimeBR(featured.published_at) : ""}</p>
            </div>
          </Link>
        </Section>
      )}

      {latestPosts.length > 0 && (
        <Section>
          <SectionHeader
            eyebrow="Últimos artigos"
            title="Publicados recentemente"
            description="Os 6 conteúdos mais recentes do time editorial."
          />

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {latestPosts.map((p) => (
              <Link
                key={p.slug}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="card-glass block overflow-hidden p-0 transition-all hover:-translate-y-1"
              >
                {p.cover_image ? (
                  <div className="aspect-[16/10] overflow-hidden bg-secondary">
                    <OptImg src={p.cover_image} alt={p.title} width={640} height={400} className="h-full w-full object-cover" />
                  </div>
                ) : null}
                <div className="p-5">
                  <span className="eyebrow">{p.category}</span>
                  <h3 className="mt-3 text-lg font-semibold leading-snug">{p.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p>
                  <p className="mt-4 text-xs text-muted-foreground">{p.published_at ? formatDateTimeBR(p.published_at) : ""}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link to="/blog" className="btn-accent">Ver todos os artigos</Link>
          </div>
        </Section>
      )}



      <Section>
        <SectionHeader
          eyebrow="Categorias"
          title="Navegue por tema"
          description="De gestão e estratégia a finanças, marketing, vendas e produtividade — organizado para você encontrar o que precisa."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {highlightCats.map((c, i) => (
            <Reveal key={c} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <Link
                to="/blog"
                className="card-glass block h-full transition-all hover:-translate-y-1"
              >
                <span className="eyebrow">Categoria</span>
                <h3 className="mt-3 text-xl font-semibold">{c}</h3>
                <p className="mt-2 text-sm text-muted-foreground">Artigos e análises sobre {c.toLowerCase()}.</p>
                <span className="mt-4 inline-block text-sm font-bold uppercase tracking-wide text-foreground border-b border-foreground pb-0.5">Ver artigos →</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow="O que publicamos"
          title="Conteúdo pensado para empresas e empreendedores"
          description="Uma linha editorial focada em ajudar quem toma decisões a agir com mais clareza."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {editorialPromises.map((p, i) => (
            <Reveal key={p.t} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <div className="card-glass h-full">
                <h3 className="text-lg font-semibold">{p.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {recentPosts.length > 0 && (
        <Section>
          <SectionHeader
            eyebrow="Mais leituras"
            title="Outros artigos recentes"
            description="Publicações atualizadas pelo time editorial."
          />

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {recentPosts.map((p) => (
              <Link
                key={p.slug}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="card-glass block overflow-hidden p-0 transition-all hover:-translate-y-1"
              >
                {p.cover_image ? (
                  <div className="aspect-[16/10] overflow-hidden bg-secondary">
                    <OptImg src={p.cover_image} alt={p.title} width={640} height={400} className="h-full w-full object-cover" />
                  </div>
                ) : null}
                <div className="p-5">
                  <span className="eyebrow">{p.category}</span>
                  <h3 className="mt-3 text-lg font-semibold leading-snug">{p.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p>
                  <p className="mt-4 text-xs text-muted-foreground">{p.published_at ? formatDateTimeBR(p.published_at) : ""}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link to="/blog" className="btn-ghost">Ver todos os artigos</Link>
          </div>
        </Section>
      )}

      <Section>
        <SectionHeader eyebrow="Por que ler o Concurso Astor" title="Como pensamos cada artigo" />
        <ul className="mt-10 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {why.map((w) => (
            <li key={w} className="card-glass flex items-start gap-3">
              <span aria-hidden className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-foreground text-background text-xs font-bold">✓</span>
              <span className="text-sm text-foreground">{w}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section>
        <SectionHeader eyebrow="Time editorial" title="Quem escreve" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <div className="card-glass flex h-full flex-col items-center text-center">
                {m.photo ? (
                  <img
                    src={m.photo}
                    alt={`Foto de ${m.name}`}
                    loading="lazy"
                    width={80}
                    height={80}
                    className="h-20 w-20 rounded-full object-cover border border-border"
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-full text-xl font-bold text-background bg-foreground" aria-hidden>{m.initials}</div>
                )}
                <h3 className="mt-4 text-base font-semibold">{m.name}</h3>
                <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{m.role}</p>
                <p className="mt-3 text-sm text-muted-foreground">{m.bio}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader eyebrow="Depoimentos" title="O que dizem os leitores" />

        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {REVIEWS.slice(0, 6).map((r) => (
            <div key={r.name} className="card-glass border-l-4 border-l-foreground">
              <p className="text-sm text-muted-foreground">"{r.text}"</p>
              <p className="mt-4 text-sm font-semibold">{r.name} <span className="text-xs font-normal text-muted-foreground">— {r.neighborhood}</span></p>
            </div>

          ))}
        </div>
      </Section>

      <Section>
        <FAQ items={faq} />
      </Section>

    </>
  );
}
